// frontend/src/pages/LoginPage.tsx
// Версия: 2.0.0 (2026-02-06) - Двухшаговый OTP flow
// ШАГ 1: Email + Пароль → "Отправить код на E-mail"
// ШАГ 2: Ввод 6-значного кода → автоматическая проверка

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { assetUrl } from '../config/paths';

const LoginPage = () => {
  const [imageError, setImageError] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Двухшаговый flow state
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [code, setCode] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [codeLoading, setCodeLoading] = useState(false);

  const codeInputRef = useRef<HTMLInputElement>(null);

  const { requestCode, verifyCode, error, loading } = useAuth();

  // Таймер для повторной отправки кода
  useEffect(() => {
    if (resendTimer <= 0) return;

    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendTimer]);

  // Автофокус на поле кода при переходе на шаг 2
  useEffect(() => {
    if (step === 2 && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [step]);

  // ШАГ 1: Отправка кода на email
  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!email.trim()) {
      setLocalError('Введите email');
      return;
    }
    if (!password) {
      setLocalError('Введите пароль');
      return;
    }

    try {
      await requestCode(email.trim(), password);
      setStep(2);
      setResendTimer(60);
      setCode('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка отправки кода';
      setLocalError(message);
    }
  };

  // ШАГ 2: Проверка кода (автоматически при 6 цифрах)
  const handleVerifyCode = async (codeValue: string) => {
    if (codeValue.length !== 6) return;

    setCodeLoading(true);
    setLocalError(null);

    try {
      await verifyCode(email.trim(), codeValue);
      // Редирект происходит в AuthContext
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Неверный код';
      setLocalError(message + '. Попробуйте снова.');
      setCode('');
    } finally {
      setCodeLoading(false);
    }
  };

  // Обработка ввода кода (только цифры)
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);

    if (value.length === 6) {
      handleVerifyCode(value);
    }
  };

  // Повторная отправка кода
  const handleResendCode = async () => {
    if (resendTimer > 0) return;

    setLocalError(null);
    try {
      await requestCode(email.trim(), password);
      setResendTimer(60);
      setCode('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка отправки кода';
      setLocalError(message);
    }
  };

  // Назад к шагу 1
  const handleBack = () => {
    setStep(1);
    setCode('');
    setLocalError(null);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        py: 4,
      }}
    >
      <Paper
        elevation={10}
        sx={{
          p: 5,
          width: { xs: '90%', sm: 450 },
          maxWidth: 450,
          borderRadius: 3,
          position: 'relative',
          overflow: 'visible',
          mt: { xs: 6, sm: 4 },
          mb: 2,
        }}
      >
        {/* ЭМБЛЕМА МОРСПАССЛУЖБЫ */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: -50, sm: -60 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 100, sm: 120 },
            height: { xs: 100, sm: 120 },
            borderRadius: '50%',
            backgroundColor: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1.5,
            animation: 'fadeIn 0.5s ease-in',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateX(-50%) scale(0.8)' },
              to: { opacity: 1, transform: 'translateX(-50%) scale(1)' },
            },
          }}
        >
          {!imageError ? (
            <img
              src={assetUrl('/sign_70.png')}
              alt="ФГБУ Морская спасательная служба"
              loading="eager"
              style={{
                width: '90%',
                height: '90%',
                objectFit: 'contain',
                imageRendering: '-webkit-optimize-contrast',
              }}
              onError={() => {
                console.error('Ошибка загрузки эмблемы sign_70.png');
                setImageError(true);
              }}
            />
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                color: '#2D2E83',
                fontWeight: 'bold',
                fontSize: { xs: '11px', sm: '14px' },
                lineHeight: 1.2,
              }}
            >
              ФГБУ
              <br />
              МОРСКАЯ
              <br />
              СПАСАТЕЛЬНАЯ
              <br />
              СЛУЖБА
            </Box>
          )}
        </Box>

        {/* Заголовки */}
        <Box sx={{ mt: { xs: 6, sm: 8 }, mb: 3, textAlign: 'center' }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: '#2D2E83',
              fontSize: { xs: '1.5rem', sm: '2rem' },
            }}
          >
            Система ССТО
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#666', fontSize: '0.95rem' }}>
            Судовая система тревожного оповещения
          </Typography>
          <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1 }}>
            ФГБУ "Морская спасательная служба"
          </Typography>
        </Box>

        {/* Сообщения об ошибках */}
        {(error || localError) && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setLocalError(null)}
          >
            {localError || error}
          </Alert>
        )}

        {/* ШАГ 1: Email + Пароль */}
        {step === 1 && (
          <Box component="form" onSubmit={handleRequestCode}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Пароль"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                />
              }
              label="Запомнить данные"
              sx={{ mt: 1, mb: 2 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Отправить код на E-mail'}
            </Button>
          </Box>
        )}

        {/* ШАГ 2: Ввод кода */}
        {step === 2 && (
          <Box>
            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: '#666' }}>
              Код отправлен на <strong>{email}</strong>
            </Typography>

            <TextField
              fullWidth
              label="Код подтверждения"
              value={code}
              onChange={handleCodeChange}
              inputRef={codeInputRef}
              inputProps={{
                maxLength: 6,
                inputMode: 'numeric',
                style: {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  letterSpacing: '0.5rem',
                  fontWeight: 600,
                },
              }}
              disabled={codeLoading}
              placeholder="000000"
              sx={{ mb: 2 }}
            />

            {codeLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}
            >
              <Link
                component="button"
                type="button"
                onClick={handleBack}
                sx={{ cursor: 'pointer' }}
              >
                ← Назад
              </Link>

              <Button onClick={handleResendCode} disabled={resendTimer > 0 || loading} size="small">
                {resendTimer > 0 ? `Повторно через ${resendTimer}с` : 'Отправить повторно'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Футер */}
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee', textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: '#999' }}>
            © 2025 ФГБУ "Морская спасательная служба"
            <br />
            Все права защищены
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginPage;
